import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { useState,useEffect,useCallback } from 'react'
import { requireCapability } from '@/lib/auth'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { useQuery,useMutation,useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api,type MaybeEnveloped } from '@/lib/api-client'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { Search,RefreshCw,X,ArrowDown,ArrowUp } from 'lucide-react'
import { useOrganizationShop } from '@/hooks/use-organization-shop'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { InventoryEmptyState } from '@/components/dashboard/inventory-empty-state'
import { TransferSidePanel } from '@/features/inventory/transfer-side-panel'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { formatInStoreTz } from '@/lib/time'

type TransferDirection = 'INCOMING' | 'OUTGOING'

interface TransferRecord {
  id: string
  manifestNumber?: string | number
  metrcId?: string
  transferType?: string
  facilityName?: string
  facilityLicense?: string
  packageCount?: number
  manifestDate?: string
  receivedDate?: string
  status?: string
  direction?: TransferDirection
  shipperFacilityName?: string
  shipperFacilityLicenseNumber?: string
  recipientFacilityName?: string
  recipientFacilityLicenseNumber?: string
  transferTypeName?: string
  createdDateTime?: string
  rawData?: Record<string, unknown>
  deliveries?: Array<{ _count?: { packages?: number } }>
}

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(20),
  q: z.string().catch(''),
  direction: z.enum(['INCOMING', 'OUTGOING']).catch('INCOMING'),
})

type SearchParams = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_dashboard/inventory/transfers')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: TransfersLayout,
})

let suppressedTransfersAutoOpenSignature: string | null = null

function TransfersLayout() {
  return <Outlet />
}

function getStatusBadge(status: string | undefined) {
  const normalized = (status || 'Unknown').toLowerCase()
  if (normalized.includes('received')) {
    return (
      <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5">
        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Received
      </Badge>
    )
  }
  if (normalized.includes('partial')) {
    return (
      <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5">
        <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Partial
      </Badge>
    )
  }
  if (normalized.includes('rejected')) {
    return (
      <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5 text-muted-foreground">
        <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
        Rejected
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5 text-muted-foreground">
      <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
      {status || 'Unknown'}
    </Badge>
  )
}

function getManifestNumber(transfer: TransferRecord): string | number {
  const raw = transfer.rawData
  if (raw?.ManifestNumber) return raw.ManifestNumber as string
  if (transfer.manifestNumber) return transfer.manifestNumber
  if (transfer.metrcId) return transfer.metrcId
  return '\u2014'
}

function getFacilityName(transfer: TransferRecord): string {
  const raw = transfer.rawData
  if (transfer.direction === 'INCOMING') {
    return (
      transfer.shipperFacilityName ??
      (raw?.ShipperFacilityName as string | undefined) ??
      transfer.shipperFacilityLicenseNumber ??
      (raw?.ShipperFacilityLicenseNumber as string | undefined) ??
      'Unknown Facility'
    )
  }
  return (
    transfer.recipientFacilityName ??
    (raw?.RecipientFacilityName as string | undefined) ??
    transfer.recipientFacilityLicenseNumber ??
    (raw?.RecipientFacilityLicenseNumber as string | undefined) ??
    'Unknown Facility'
  )
}

function getFacilityLicense(transfer: TransferRecord): string | null {
  const raw = transfer.rawData
  if (transfer.direction === 'INCOMING') {
    return transfer.shipperFacilityLicenseNumber ?? (raw?.ShipperFacilityLicenseNumber as string | undefined) ?? null
  }
  return transfer.recipientFacilityLicenseNumber ?? (raw?.RecipientFacilityLicenseNumber as string | undefined) ?? null
}

function getTransferType(transfer: TransferRecord): string {
  const raw = transfer.rawData
  return transfer.transferTypeName ?? (raw?.ShipmentTypeName as string | undefined) ?? transfer.transferType ?? 'Transfer'
}

function getPackageCount(transfer: TransferRecord): number {
  if (typeof transfer.packageCount === 'number') return transfer.packageCount
  const rawCount = transfer.rawData?.PackageCount
  if (typeof rawCount === 'number') return rawCount
  const deliveryPackages = transfer.deliveries?.reduce((sum, d) => sum + (d._count?.packages ?? 0), 0)
  if (deliveryPackages !== undefined) return deliveryPackages
  return 0
}

function getStatusLabel(transfer: TransferRecord): string {
  const raw = transfer.rawData
  if (raw?.IsVoided) return 'Voided'
  if (raw?.ReceivedDateTime || transfer.receivedDate) return 'Received'
  const receivedPkgCount = raw?.ReceivedPackageCount as number | undefined
  const pkgCount = raw?.PackageCount as number | undefined
  if (receivedPkgCount !== undefined && pkgCount !== undefined) {
    if (receivedPkgCount > 0 && receivedPkgCount < pkgCount) return 'Partial'
    if (receivedPkgCount === 0 && pkgCount > 0) return 'In Transit'
  }
  return transfer.status ?? 'Unknown'
}

function getDateLabel(transfer: TransferRecord): string {
  const raw = transfer.rawData
  const dateValue =
    transfer.receivedDate ??
    (raw?.ReceivedDateTime as string | undefined) ??
    transfer.createdDateTime ??
    (raw?.CreatedDateTime as string | undefined) ??
    (raw?.EstimatedArrivalDateTime as string | undefined) ??
    transfer.manifestDate
  return dateValue ? formatInStoreTz(dateValue, 'MMM dd, yyyy') : 'N/A'
}

export function TransfersPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, q, direction } = useSearch({ from: Route.id }) as SearchParams
  const { shopId, isLoading: shopLoading } = useOrganizationShop()
  const queryClient = useQueryClient()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${q}:${direction}`

  const [searchInput, setSearchInput] = useState(q)
  const [lastSynced, setLastSynced] = useState<Date | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const transfersQuery = useQuery({
    queryKey: queryKeys.transfers(shopId!, { page, limit, q, direction }),
    queryFn: async () => {
      const params: Record<string, string | number> = { shopId: shopId!, page, limit, direction }
      if (q) params.search = q
      const response = await api.get<TransferRecord[] & { data?: TransferRecord[]; meta?: Record<string, unknown> }>(`/metrc/transfers`, { params })
      // The intersection type makes the non-array branch `never`; cast keeps the legacy fallback compiling unchanged.
      const data: TransferRecord[] = Array.isArray(response) ? response : ((response as { data?: TransferRecord[] }).data ?? [])

      const term = q.trim().toLowerCase()
      let filteredData = data
      if (term) {
        filteredData = data.filter((transfer) => {
          const manifest = String(transfer.rawData?.ManifestNumber ?? transfer.manifestNumber ?? transfer.metrcId ?? '').toLowerCase()
          const facility =
            transfer.direction === 'OUTGOING'
              ? String(transfer.recipientFacilityName ?? transfer.rawData?.RecipientFacilityName ?? '')
              : String(transfer.shipperFacilityName ?? transfer.rawData?.ShipperFacilityName ?? '')
          const license =
            transfer.direction === 'OUTGOING'
              ? String(transfer.recipientFacilityLicenseNumber ?? transfer.rawData?.RecipientFacilityLicenseNumber ?? '')
              : String(transfer.shipperFacilityLicenseNumber ?? transfer.rawData?.ShipperFacilityLicenseNumber ?? '')
          const type = String(transfer.transferTypeName ?? transfer.rawData?.ShipmentTypeName ?? transfer.transferType ?? '')
          return [manifest, facility, license, type].some((v) => v.toLowerCase().includes(term))
        })
      }

      const meta = response.meta as Record<string, unknown> | undefined
      const total = meta && !term ? ((meta.total as number) ?? 0) : filteredData.length
      const totalPagesVal = meta && !term ? ((meta.totalPages as number) ?? 1) : 1

      return { data: filteredData, meta: { total, totalPages: totalPagesVal } }
    },
    enabled: !!shopId && !shopLoading,
  })

  const syncMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/metrc/transfers/sync`, null, { params: { shopId } })
    },
    onSuccess: () => {
      setLastSynced(new Date())
      queryClient.invalidateQueries({ queryKey: queryKeys.transfers(shopId!) })
      toast.success('Sync complete', { description: 'Successfully synced transfers from Metrc.' })
    },
    onError: () => {
      toast.error('Sync failed', { description: 'Could not sync transfers. Please try again.' })
    },
  })

  const transfers = transfersQuery.data?.data ?? []
  const totalItems = transfersQuery.data?.meta.total ?? 0
  const totalPages = transfersQuery.data?.meta.totalPages ?? 1
  const showLoading = transfersQuery.isLoading || shopLoading
  const currentPageHasSelectedTransfer = !!selectedId && transfers.some((transfer) => transfer.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedTransfer) return selectedId
    if (showLoading || transfers.length === 0) return undefined
    if (!selectedId && suppressedTransfersAutoOpenSignature === pageSignature) return undefined
    return transfers[0].id
  })()

  const directTransferQuery = useQuery({
    queryKey: queryKeys.transfer(effectiveSelectedId ?? selectedId),
    queryFn: async () => {
      const res = await api.get<MaybeEnveloped<TransferRecord>>(`/metrc/transfers/${effectiveSelectedId ?? selectedId}`)
      return (res?.data ?? res) as TransferRecord
    },
    enabled: !!(effectiveSelectedId ?? selectedId) && !transfers.find((t) => t.id === (effectiveSelectedId ?? selectedId)),
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== q) {
        navigate({ to: '.', search: (prev) => ({ ...prev, q: searchInput, page: 1 }) })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, q, navigate])

  const selectedTransfer = transfers.find((t) => t.id === effectiveSelectedId) ?? directTransferQuery.data ?? null

  const setSelectedId = useCallback((id: string | null) => {
    if (id) {
      navigate({
        to: '/inventory/transfers/$item',
        params: { item: id },
        search: (prev) => ({ ...prev }),
      })
      return
    }

    navigate({ to: '/inventory/transfers', search: (prev) => ({ ...prev }) })
  }, [navigate])

  const closeSelectedTransfer = useCallback(() => {
    suppressedTransfersAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/inventory/transfers')) {
          suppressedTransfersAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  return (
    <div className="space-y-6 h-full">
      <PageBreadcrumb />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0 gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Transfers</h2>
          <p className="text-muted-foreground mt-1">Incoming and outgoing transfer manifests from Metrc.</p>
        </div>
        <div className="flex items-center space-x-2 text-sm self-start sm:self-auto">
          <span className="text-muted-foreground">
            {lastSynced ? `Synced ${formatDistanceToNow(lastSynced)} ago` : 'Ready to sync'}
          </span>
          <div className={cn('h-1.5 w-1.5 rounded-full mr-2', lastSynced ? 'bg-emerald-500' : 'bg-muted-foreground')} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || !shopId}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', syncMutation.isPending && 'animate-spin')} />
            {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-sm w-full group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search by manifest #, facility..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void transfersQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={direction === 'INCOMING' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, direction: 'INCOMING' as const, page: 1 }) })}
          >
            <ArrowDown className="mr-2 h-4 w-4" />
            Incoming
          </Button>
          <Button
            variant={direction === 'OUTGOING' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, direction: 'OUTGOING' as const, page: 1 }) })}
          >
            <ArrowUp className="mr-2 h-4 w-4" />
            Outgoing
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div
          className={cn(
            'flex-1 min-w-0 transition-all duration-300',
            !showLoading && transfers.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden',
            selectedTransfer ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full',
          )}
        >
          {!showLoading && transfers.length === 0 ? (
            <InventoryEmptyState
              title="No transfers found"
              description="Transfers will appear here once synced from Metrc."
            />
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-14rem)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[120px]">Manifest #</TableHead>
                      <TableHead>From / To</TableHead>
                      <TableHead className="w-[140px]">Type</TableHead>
                      <TableHead className="w-[100px] text-right">Packages</TableHead>
                      <TableHead className="w-[140px]">Date</TableHead>
                      <TableHead className="w-[140px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {showLoading
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                            <TableCell>
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-40" />
                                <Skeleton className="h-3 w-28" />
                              </div>
                            </TableCell>
                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                            <TableCell className="text-right"><Skeleton className="h-4 w-10 ml-auto" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                          </TableRow>
                        ))
                      : transfers.map((transfer) => {
                          const manifest = getManifestNumber(transfer)
                          const facility = getFacilityName(transfer)
                          const license = getFacilityLicense(transfer)
                          const transferType = getTransferType(transfer)
                          const packageCount = getPackageCount(transfer)
                          const dateLabel = getDateLabel(transfer)
                          const statusLabel = getStatusLabel(transfer)

                          return (
                            <TableRow
                              key={transfer.id}
                              className={cn(
                                'cursor-pointer hover:bg-muted/50 transition-colors',
                                effectiveSelectedId === transfer.id && 'bg-muted/80',
                              )}
                              onClick={() => {
                                if (effectiveSelectedId === transfer.id) {
                                  closeSelectedTransfer()
                                  return
                                }
                                setSelectedId(transfer.id)
                              }}
                            >
                              <TableCell className="font-mono text-xs">#{String(manifest)}</TableCell>
                              <TableCell>
                                <div className="font-medium truncate max-w-[220px]" title={facility}>
                                  {facility}
                                </div>
                                {license && (
                                  <div className="text-xs text-muted-foreground font-mono truncate max-w-[220px]">
                                    {license}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-muted-foreground">{transferType}</TableCell>
                              <TableCell className="text-right font-medium">{packageCount}</TableCell>
                              <TableCell className="text-muted-foreground text-xs font-medium">{dateLabel}</TableCell>
                              <TableCell>{getStatusBadge(statusLabel)}</TableCell>
                            </TableRow>
                          )
                        })}
                  </TableBody>
                </Table>
              </div>

              <DataTablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                entityName="transfers"
                isLoading={transfersQuery.isLoading}
                onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
                onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
              />
            </>
          )}
        </div>

        {selectedTransfer && (
          <div className="w-full md:w-[320px] lg:w-[360px] sticky top-0 shrink-0 mt-0 flex flex-col h-fit max-h-[calc(100vh-6rem)]">
            <TransferSidePanel
              open={!!selectedTransfer}
              onOpenChange={(open: boolean) => {
                if (!open) closeSelectedTransfer()
              }}
              transfer={selectedTransfer}
            />
          </div>
        )}
      </div>
    </div>
  )
}
